/**
 * Dependencies.
 */
import { BaseError } from '@prz-dp/services';
import { Component, Mixins } from 'vue-property-decorator';

import { orderRequestService } from '@/api/order-request/order-request.service';
import { ORDER_REQUEST_TYPES } from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import Error from '@/ui/components/error.component.vue';
import Loader from '@/ui/components/loader.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: { Error, Loader, NotifyClientRequest, Timeline },
  name: 'confirm-change-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `isSavingChange`.
   */
  isSavingChange = true;

  /**
   * Property `isErrorSaving`.
   */
  isErrorSaving = false;

  /**
   * Property `requestApprovedAutomatically`.
   */
  requestApprovedAutomatically = false;

  /**
   * Getter status title.
   */
  get statusTitle() {
    return this.i18n('TRACKING.SENTENCES.STATUS_HELLO', false, 'upperFirst', {
      name: this.client || '-'
    });
  }

  /**
   * Getter status sentence.
   */
  get statusSentence() {
    let key = 'UPDATING_CHANGE';

    if (!this.isSavingChange) {
      const { type = null } = this.$route.params || {};

      if (type === 'contact') {
        key = 'REQUEST_CONTACT_SUBMITTED';
      } else if (this.requestApprovedAutomatically) {
        key = 'CONFIRM_CHANGE_AUTOMATIC';
      } else {
        key = 'CONFIRM_CHANGE';
      }
    }

    if (this.isErrorSaving) {
      key = 'CONFIRM_ERROR';
    }

    return this.i18n(`TRACKING.SENTENCES.${key}`);
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    await this.handleCreateRequest();
  }

  /**
   * Handle create request
   */
  async handleCreateRequest() {
    try {
      const { id: orderId = null } = this.order || {};

      if (!orderId) {
        this.handleForceTrackingScene();

        throw new BaseError(
          'ConfirmChangeScene',
          'handleCreateRequest',
          'Trying to create request without id'
        );
      }

      const { type = null } = this.$route.params || {};
      const {
        addressChanged: address = null,
        dayChanged: date = null,
        order = null
      } = trackingStore;
      const { address: oldAddress = null } = order || {};
      const { countryCode, countryName } = oldAddress || {};
      const {
        additionalInformation,
        city,
        doorNumber,
        isForAutomaticApproval: isAutomatic = false,
        latitude,
        longitude,
        servicePointCode,
        street,
        zipCode
      } = address || {};

      let newAddress: {
        additionalInformation: string;
        city: string;
        countryCode: string;
        countryName: string;
        doorNumber: number;
        servicePointCode?: string;
        latitude: number;
        longitude: number;
        street: string;
        zipCode: string;
      } = null;

      if (address) {
        newAddress = {
          additionalInformation,
          city,
          countryCode,
          countryName,
          doorNumber,
          latitude,
          longitude,
          street,
          zipCode
        };

        if (servicePointCode) {
          newAddress = {
            ...newAddress,
            servicePointCode
          };
        }
      }

      let data: {
        collectId?: number;
        deliveryId?: number;
        isAutomatic?: boolean;
        orderType: 'DELIVERY' | 'COLLECT';
        request?: string;
        type: string;
      } = null;

      const requestPendingTreatment = {
        key: null,
        value: true
      };

      const { isDelivery, orderType } = this || {};
      const orderIdentification = {
        [isDelivery ? 'deliveryId' : 'collectId']: orderId,
        orderType
      };

      switch (type) {
        case 'contact':
          requestPendingTreatment.key = 'contact';

          data = {
            ...orderIdentification,
            type: ORDER_REQUEST_TYPES.CONTACT
          };
          break;

        case 'address':
          if (!newAddress) {
            throw new BaseError(
              'ConfirmChangeScene',
              'handleCreateRequest',
              `Trying to create request of type ${ORDER_REQUEST_TYPES.CHANGE_ADDRESS} without address`
            );
          }

          requestPendingTreatment.key = 'changeAddress';

          data = {
            ...orderIdentification,
            isAutomatic,
            request: JSON.stringify({
              address: newAddress
            }),
            type: ORDER_REQUEST_TYPES.CHANGE_ADDRESS
          };
          break;

        case 'day':
          if (!date) {
            throw new BaseError(
              'ConfirmChangeScene',
              'handleCreateRequest',
              `Trying to create request of type ${ORDER_REQUEST_TYPES.CHANGE_DATE} without date`
            );
          }

          requestPendingTreatment.key = 'changeDate';

          data = {
            ...orderIdentification,
            isAutomatic: true,
            request: JSON.stringify({ date }),
            type: ORDER_REQUEST_TYPES.CHANGE_DATE
          };
          break;

        case 'day_address':
          if (!newAddress) {
            throw new BaseError(
              'ConfirmChangeScene',
              'handleCreateRequest',
              `Trying to create request of type ${ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS} without address`
            );
          }

          if (!date) {
            throw new BaseError(
              'ConfirmChangeScene',
              'handleCreateRequest',
              `Trying to create request of type ${ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS} without date`
            );
          }

          requestPendingTreatment.key = 'changeDateAndAddress';

          data = {
            ...orderIdentification,
            isAutomatic: true,
            request: JSON.stringify({
              address: newAddress,
              date
            }),
            type: ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS
          };
          break;

        default:
          throw new BaseError(
            'ConfirmChangeScene',
            'handleCreateRequest',
            'Trying to create request without type'
          );
      }

      const {
        isAutomatic: requestApprovedAutomatically = false
      }: { isAutomatic: boolean } = await orderRequestService.POST(
        orderRequestService.getModelEndpoint,
        data
      );

      this.requestApprovedAutomatically = requestApprovedAutomatically;

      trackingStore.setRequestPendingTreatment(requestPendingTreatment);
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error(error);
      }

      this.isErrorSaving = true;
    } finally {
      trackingStore.setChangeDeliveryAddress({ address: null });
      trackingStore.setChangeDeliveryDay({ day: null });
      trackingStore.setRequestMessage({ message: null });

      this.isSavingChange = false;
    }
  }

  /**
   * Hande confirm leave.
   */
  async handleConfirmLeave() {
    // Get data to update history.
    await this.handleGetDeliveryData();

    this.$router.push({
      name: 'Tracking',
      params: {
        key: this.key
      }
    });
  }
}
